import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { getRelayOrderDetailsApi } from 'api/relay';
import { handleError } from 'store/thunks/errorHandlerThunks';

import RelayEventTimeline from './RelayEventTimeline';
import RelayLineItemsTable from './RelayLineItemsTable';
import RelayOrderSummary from './RelayOrderSummary';

/**
 * POM-6099 — one relayed order, for a POM sys-admin.
 *
 * THERE IS NO PAYLOAD VIEW AND THERE MUST NOT BE ONE. The plan for this story specified tabs
 * "Line items / Request Payload / Raw / Events" modelled on RxIntakePayloadViewDialog, and core
 * exposes nothing to fill the middle two: no prescription payload, no submitted request body, no
 * vendor response, no webhook body. That was decided in story 9, and the reasoning is what makes
 * it stick — in the Rx Intake queue the payload IS the work product, read and corrected and
 * imported by licensed clinical staff, so disclosing it is the task. Here nobody at PerfectRx
 * reviews the prescription; Diamond dispenses it, and the operator's questions are "did it reach
 * Diamond, why not, can I retry". Same widget, different task. The component may be reused; the
 * disclosure may not.
 *
 * If a future screen genuinely needs the body, that is an API change in core with its own review,
 * not a field quietly added to a portal query.
 */

const TABS = {
  SUMMARY: 'SUMMARY',
  LINE_ITEMS: 'LINE_ITEMS',
  TIMELINE: 'TIMELINE',
};

const RelayOrderDetailsDialog = ({ relayOrderId, onClose }) => {
  const dispatch = useDispatch();
  const [details, setDetails] = useState(undefined);
  const [tab, setTab] = useState(TABS.SUMMARY);

  useEffect(() => {
    // Cleared on the way OUT as well as in. The dialog stays mounted, so leaving the previous
    // order's details in state means the next order opens showing the LAST patient's ship-to
    // address for a frame -- the new prop renders before this effect runs. That is one patient's
    // address displayed under another patient's order.
    setDetails(undefined);
    setTab(TABS.SUMMARY);

    if (!relayOrderId) {
      return undefined;
    }

    // Open A (slow), close, open B (fast): without this guard A's response lands last and the
    // dialog sits on A's address and operator notes while showing B's title, with nothing to
    // indicate it. The parent also keys this component by order id, so both halves hold.
    let cancelled = false;

    getRelayOrderDetailsApi(relayOrderId)
      .then((result) => {
        if (!cancelled) {
          setDetails(result);
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setDetails(null);
        dispatch(handleError({ error }));
      });

    return () => {
      cancelled = true;
    };
  }, [relayOrderId, dispatch]);

  const handleTabChange = useCallback((_event, next) => setTab(next), []);

  if (!relayOrderId) {
    return null;
  }

  const order = details?.order;

  return (
    <Dialog open fullWidth maxWidth='lg' onClose={onClose}>
      <DialogTitle>Relayed Order {order ? `— ${order.externalOrderId}` : ''}</DialogTitle>
      <DialogContent dividers>
        {details === undefined && <Typography variant='body2'>Loading…</Typography>}

        {details === null && (
          <Alert severity='warning'>This relayed order could not be found.</Alert>
        )}

        {order && (
          <>
            <Tabs value={tab} onChange={handleTabChange} aria-label='relayed order sections'>
              <Tab value={TABS.SUMMARY} label='Summary' />
              <Tab value={TABS.LINE_ITEMS} label='Line Items' />
              <Tab value={TABS.TIMELINE} label='Timeline' />
            </Tabs>

            {tab === TABS.SUMMARY && (
              <RelayOrderSummary order={order} documentCount={details.lineItems.length} />
            )}

            {tab === TABS.LINE_ITEMS && (
              <RelayLineItemsTable
                lineItems={details.lineItems}
                deliveryMethod={order.deliveryMethod}
              />
            )}

            {tab === TABS.TIMELINE && (
              <RelayEventTimeline events={details.events} hasMoreEvents={details.hasMoreEvents} />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

RelayOrderDetailsDialog.propTypes = {
  relayOrderId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

RelayOrderDetailsDialog.defaultProps = { relayOrderId: null };

export default RelayOrderDetailsDialog;
